import React, { ReactElement, useEffect, Suspense } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';

import { useAppDispatch } from 'app/hooks';
import { useAuthStateChanged } from 'app/services/firebase/auth';

import { changeType } from 'app/services/redux/slices';

import { ROUTES } from 'app/constants';

import { PrivateRoute } from 'app/components/common';
import { debounce, getDeviceTypeByWidth } from 'app/utils';

import DashboardLayout from 'app/components/DashboardLayout/DashboardLayout';
import PersonalDashboard from 'app/components/pages/Personal/Dashboard/Dashboard';
import TourViewRoute from 'app/components/pages/Personal/Dashboard/TourViewRoute';
import PersonalProfileEdit from 'app/components/pages/Personal/EditProfile/EditProfile';
import Insights from 'app/components/pages/Personal/Insights/Insights';
import TourUpload from 'app/components/pages/Personal/Upload/Upload';

const SignUp = React.lazy(
  () => import('app/components/pages/Authentication/SignUp/SignUp'),
);
const SignIn = React.lazy(
  () => import('app/components/pages/Authentication/SignIn/SignIn'),
);
const ForgetPassword = React.lazy(
  () =>
    import('app/components/pages/Authentication/ForgetPassword/ForgetPassword'),
);
const SignInModal = React.lazy(
  () =>
    import(
      'app/components/pages/Authentication/SignIn/SignInModal/SignInModal'
    ),
);

function App(): ReactElement {
  const dispatch = useAppDispatch();

  useEffect(() => {
    const setDeviceType = debounce(() => {
      const width = window.screen.width;

      dispatch(changeType(getDeviceTypeByWidth(width)));
    }, 500);

    window.addEventListener('resize', setDeviceType);

    return () => window.removeEventListener('resize', setDeviceType);
  }, [dispatch]);

  useAuthStateChanged();

  return (
    <Suspense fallback={null}>
      <Router>
        <Routes>
          <Route element={<DashboardLayout />}>
            <Route
              path={ROUTES.PERSONAL_DASHBOARD}
              element={
                <PrivateRoute>
                  <PersonalDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.PERSONAL_TOUR_VIEW}
              element={
                <PrivateRoute>
                  <TourViewRoute />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.PERSONAL_INSIGHTS}
              element={
                <PrivateRoute>
                  <Insights />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.PERSONAL_TOUR_UPLOAD}
              element={
                <PrivateRoute>
                  <TourUpload />
                </PrivateRoute>
              }
            />
            <Route
              path={ROUTES.PERSONAL_PROFILE_EDIT}
              element={
                <PrivateRoute>
                  <PersonalProfileEdit />
                </PrivateRoute>
              }
            />
          </Route>

          <Route path={ROUTES.SIGN_IN} element={<SignIn />} />
          <Route path={ROUTES.SIGN_UP} element={<SignUp />} />
          <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgetPassword />} />

          <Route
            path="*"
            element={<Navigate to={ROUTES.PERSONAL_DASHBOARD} replace />}
          />
        </Routes>
        <SignInModal />
      </Router>
    </Suspense>
  );
}

export default App;
